import { useState, useEffect, useCallback } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { formatDateForStorage } from '../../../../Helpers/DateUtils';
import { resolveComponentSchedule, templateLookup } from '../../../../Helpers/maintenanceSchedule';
import { BaseComponentImpl } from '../../../../types/implementations';
import { Building } from '../../../../types/protocols';
import { ComponentLifetime, MaintenanceTemplate } from '../../TemplatesEditor/types';
import type { MaintenanceDraftWithDone } from '../types';
import type { MaintenancePlan, ComponentMaintenanceData, MaintenanceActivity } from '../../types';
import { useToast } from '../../../../Components/ToastContext';
import { useBuildComponentTypes } from '../../../../contexts/BuildComponentTypesContext';
import { assessmentInstanceUpdates, AssessmentTemplates } from '../../../../api/buildingChecksApi';
import type { BuildingCheck } from '../../../BuildingChecks/buildingChecksUtils';
import { AssessmentScheduleDrafts, groupsWithLiveComponents } from '../../../../Helpers/assessmentGroups';
import { formatDateOnlyForStorage } from '../../../../Helpers/DateUtils';
import { withCompletionHistory } from '../utils/draftFromActive';
import { maintenancePlanApi } from '../../../../api/maintenancePlanApi';

/**
 * Validates that Firebase data conforms to MaintenancePlan structure.
 * Returns null if validation fails.
 */
const parseMaintenancePlan = (data: unknown): MaintenancePlan | null => {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;

    // Validate required structure
    if (!d.components || typeof d.components !== 'object') {
        console.warn('Invalid MaintenancePlan: missing components');
        return null;
    }

    // Validate metadata exists
    if (!d.metadata || typeof d.metadata !== 'object') {
        console.warn('Invalid MaintenancePlan: missing metadata');
        return null;
    }

    return data as MaintenancePlan;
};

interface UseActivatePlanProps {
    buildingKey: string | undefined;
    maintenanceDraft: MaintenanceDraftWithDone;
    building: Building | null;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: Record<string, MaintenanceTemplate[]>;
}

/** The Skickbedömning schedule as the activation dialog showed it and the user confirmed it. */
export interface ActivationAssessment {
    /** The groups the user edited in the dialog */
    staged: AssessmentScheduleDrafts;
    /** `inspections/active/{b}` keyed by check key (Skickbedömning: the fixed conditionAssessment_{group}) */
    instances: Record<string, BuildingCheck>;
    /** The three Skickbedömning system templates that exist; any missing ⇒ nothing is scheduled (D5) */
    templates: AssessmentTemplates;
}

interface UseActivatePlanReturn {
    activatePlan: (assessment: ActivationAssessment) => Promise<void>;
    activating: boolean;
    hasExistingPlan: boolean;
    checkingExistingPlan: boolean;
    activePlan: MaintenancePlan | null;
}

/**
 * Hook to handle activation of a maintenance plan from draft
 */
export const useActivatePlan = ({
    buildingKey,
    maintenanceDraft,
    building,
    componentLifetimes,
    maintenanceTemplates
}: UseActivatePlanProps): UseActivatePlanReturn => {
    const { types: componentTypeDefinitions } = useBuildComponentTypes();
    const [activating, setActivating] = useState(false);
    const [hasExistingPlan, setHasExistingPlan] = useState(false);
    const [checkingExistingPlan, setCheckingExistingPlan] = useState(true);
    const [activePlan, setActivePlan] = useState<MaintenancePlan | null>(null);

    const db = getDatabase();
    const auth = getAuth();
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Check if an active plan already exists and load it
    useEffect(() => {
        const checkExistingPlan = async () => {
            if (!buildingKey) {
                setCheckingExistingPlan(false);
                return;
            }

            try {
                const activeSnapshot = await get(ref(db, `maintenance/active/${buildingKey}`));
                if (activeSnapshot.exists()) {
                    const parsedPlan = parseMaintenancePlan(activeSnapshot.val());
                    if (parsedPlan) {
                        setHasExistingPlan(true);
                        setActivePlan(parsedPlan);
                    } else {
                        // Data exists but is invalid
                        console.warn(`Invalid active plan data for building ${buildingKey}`);
                        setHasExistingPlan(false);
                        setActivePlan(null);
                    }
                } else {
                    setHasExistingPlan(false);
                    setActivePlan(null);
                }
            } catch (error) {
                console.error('Error checking existing plan:', error);
                setHasExistingPlan(false);
                setActivePlan(null);
            } finally {
                setCheckingExistingPlan(false);
            }
        };

        checkExistingPlan();
    }, [buildingKey, db]);

    const activatePlan = useCallback(async (assessment: ActivationAssessment): Promise<void> => {
        if (!maintenanceDraft || !buildingKey) return;

        // Validate that the draft has components
        if (!maintenanceDraft.components || Object.keys(maintenanceDraft.components).length === 0) {
            showToast('Underhållsplanen innehåller inga komponenter.', 'danger');
            return;
        }

        setActivating(true);
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('Du måste vara inloggad för att aktivera underhållsplanen');
            }

            // The active plan is the draft as the preview showed it: every
            // activity resolved through the shared schedule (template found by
            // id, dates and intervals derived where the entry lacks them, overdue
            // replacement pulled to this year) with template costs snapshotted.
            // The API passes the current active plan read fresh: work orders
            // completed while the draft was open advanced it
            const buildPlan = (current: MaintenancePlan | null): MaintenancePlan => {
                const previousComponents: MaintenancePlan['components'] = current?.components ?? {};
                const filteredComponents: Record<string, ComponentMaintenanceData> = {};
                const findTemplate = templateLookup(maintenanceTemplates);
                const currentYear = new Date().getFullYear();

                Object.entries(maintenanceDraft.components).forEach(([componentKey, componentData]) => {
                    // Skip excluded components entirely
                    if (componentData.excluded === true) {
                        return;
                    }

                    const buildingComponent = building?.model?.structure?.components?.[componentKey];
                    const component = buildingComponent ? new BaseComponentImpl(buildingComponent) : null;
                    const componentType = component?.specificType?.value;
                    const schedule = component && componentType && building
                        ? resolveComponentSchedule({
                            component,
                            entry: componentData,
                            building,
                            lifetime: componentLifetimes[componentType],
                            typeTemplates: maintenanceTemplates[componentType] || [],
                            findTemplate,
                            window: { startYear: currentYear, endYear: currentYear + 1 },
                            isDraft: true
                        })
                        : null;

                    if (!schedule) {
                        // The component is gone from the building: keep the entry as it is, minus disabled activities
                        const kept: Record<string, MaintenanceActivity> = {};
                        Object.entries(componentData.maintenanceActivities || {}).forEach(([templateId, activity]) => {
                            if (activity.disabled !== true) kept[templateId] = activity;
                        });
                        filteredComponents[componentKey] = { ...componentData, maintenanceActivities: kept };
                        return;
                    }

                    const maintenanceActivities: Record<string, MaintenanceActivity> = {};
                    schedule.activities.forEach(activity => {
                        const stored = componentData.maintenanceActivities?.[activity.templateId];
                        maintenanceActivities[activity.templateId] = withCompletionHistory({
                            ...stored,
                            nextDate: activity.firstDate,
                            intervalType: activity.intervalType,
                            intervalValue: activity.intervalValue,
                            cost: activity.cost,
                            costSource: activity.costSource,
                            lastUpdated: stored?.lastUpdated || formatDateForStorage(new Date())
                        }, previousComponents[componentKey]?.maintenanceActivities?.[activity.templateId]);
                    });

                    const snapshot: ComponentMaintenanceData = {
                        ...componentData,
                        maintenanceActivities
                    };
                    if (schedule.replacement) {
                        snapshot.replacementYear = schedule.replacement.firstYear;
                        snapshot.replacementCost = schedule.replacement.cost;
                        snapshot.replacementCostSource = schedule.replacement.costSource;
                    }

                    filteredComponents[componentKey] = snapshot;
                });

                return {
                    components: filteredComponents,
                    metadata: {
                        ...maintenanceDraft.metadata,
                        version: maintenanceDraft.metadata.version || '1.0'
                    }
                };
            };

            // Schedule Skickbedömning as building checks (plan §3.4). Empty when any system
            // template is missing: the plan still activates, the dialog has already said so.
            const now = new Date();
            const extraUpdates = assessmentInstanceUpdates({
                buildingKey,
                groups: groupsWithLiveComponents(building, componentTypeDefinitions),
                staged: assessment.staged,
                instances: assessment.instances,
                templates: assessment.templates,
                uid: user.uid,
                today: formatDateOnlyForStorage(now),
                now: formatDateForStorage(now)
            });

            // Archives the current plan, writes the new one and removes the draft in one update (admin only)
            await maintenancePlanApi.activatePlan({ buildingKey, buildPlan, extraUpdates });

            showToast('Underhållsplanen har aktiverats!', 'success');

            // Navigate to active plan page
            navigate(`/maintenanceActive/${buildingKey}`);

        } catch (error) {
            console.error('Error activating plan:', error);
            showToast('Ett fel uppstod vid aktivering av planen. Försök igen.', 'danger');
        } finally {
            setActivating(false);
        }
    }, [
        maintenanceDraft,
        buildingKey,
        building,
        componentLifetimes,
        maintenanceTemplates,
        componentTypeDefinitions,
        auth,
        navigate,
        showToast
    ]);

    return {
        activatePlan,
        activating,
        hasExistingPlan,
        checkingExistingPlan,
        activePlan
    };
};
